import { pushToDatalayer } from '@luks/meta/src';
import {
  buildForm,
  buildLink,
  KitchenSinkRoute,
  RouteSlotInput,
} from '@luks/ui';
import { memoize } from 'lodash';

import {
  EventsSearchQuery,
  LiveEventFragment,
  useInfiniteEventsSearchQuery,
} from '../../../generated/queries';
import { formNavigate, useQueryParameters } from '../../commons/forms';
import { buildDrupalImage } from '../Image';

type Filters = {
  title: string;
  type: string;
  date: string;
  location: string;
};

const Form = buildForm<Filters>({
  initialValues: {
    title: '',
    type: '',
    date: '',
    location: '',
  },
  onSubmit: formNavigate(),
  useInitialValues: useQueryParameters,
});

type EventInput = Exclude<
  RouteSlotInput<typeof KitchenSinkRoute, 'body', 'EventSearch'>['input'],
  Function
>['searchTeasers'][number];

export const mapEvent = memoize(
  (item: LiveEventFragment): EventInput => {
    return {
      Image: buildDrupalImage(item.image, 'teaser'),
      Link: buildLink({ href: item.path }),
      title: item.title,
      startDate: item.startDate,
      startTime: item.startTime,
      endDate: item.endDate,
      endTime: item.endTime,
      location: item.location,
      eventStatus: item.eventStatus,
    };
  },
  (item) => `event-${item.id}`,
);

const aggregateResults = memoize((pages?: Array<EventsSearchQuery>) =>
  (
    pages?.reduce(
      (acc, value) => [...acc, ...value.searchEvents.items],
      [] as Array<LiveEventFragment>,
    ) || []
  ).map(mapEvent),
);

type Slot = RouteSlotInput<typeof KitchenSinkRoute, 'body', 'EventSearch'>;
type SlotInput = Exclude<Slot['input'], Function>;

export function mapEventSearch(
  input: EventSearchFragment,
  context: { items: Array<any>; index: number },
): Slot {
  return {
    key: 'EventSearch',
    input: function useEventSearch() {
      const pageSize = 12;
      const params = useQueryParameters<Filters>();

      const {
        data,
        isLoading,
        isFetching,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage,
        isError,
      } = useInfiniteEventsSearchQuery(
        {
          filters: params || {},
          limit: pageSize,
          offset: 0,
        },

        {
          keepPreviousData: true,
          getNextPageParam: (lastPage, allPages) => {
            const displayedItems = allPages.reduce(
              (acc, val) => acc + (val.searchEvents.items.length || 0),
              0,
            );

            return lastPage.searchEvents.total > displayedItems
              ? {
                  limit: pageSize,
                  filters: params,
                  offset: displayedItems,
                }
              : undefined;
          },
        },
      );

      if (
        data &&
        data.pages.length &&
        !isLoading &&
        !isFetching &&
        !isFetchingNextPage
      ) {
        const filterSearchTerm = params?.title;
        const filterType = input.filters.type.find(
          (it) => it.id === params?.type,
        )?.title;
        const filterDate = params?.date;
        const filterLocation = input.filters.location.find(
          (it) => it.id === params?.location,
        )?.title;
        const hasFilters =
          filterSearchTerm || filterType || filterDate || filterLocation;
        pushToDatalayer(
          {
            event: 'EventSearch',
            gtmFilters: {
              gtmSearchTerm: filterSearchTerm,
              gtmType: filterType,
              gtmDate: filterDate,
              gtmLocation: filterLocation,
            },
            currentResultsShown: data.pages
              .map((page) => page.searchEvents.items.length)
              .reduce((acc, val) => acc + val, 0),
            totalResultSet: data.pages.at(-1)?.searchEvents.total,
            currentPageNumber: data.pages.length,
            firstPageLoad: !hasFilters && data.pages.length === 1,
          },
          'event-search',
        );
      }

      const latest = data?.pages.at(-1);
      return [
        {
          searchTeasers: aggregateResults(data?.pages),
          types: mapTypes(input.filters.type),
          locations: input.filters.location.map((item) => ({
            value: item.id,
            title: item.title,
          })),
          Form,
          infiniteScroll: context.index === context.items.length - 1,
          MoreLink: hasNextPage
            ? buildLink({
                onClick: (ev) => {
                  ev.preventDefault();
                  fetchNextPage();
                },
              })
            : undefined,
        },
        isError
          ? 500
          : isLoading || isFetching || isFetchingNextPage
          ? 102
          : data
          ? latest?.searchEvents?.total || 0 > 0
            ? 200
            : 404
          : 0,
      ];
    },
  };
}

const mapTypes = (
  types: EventSearchFragment['filters']['type'],
): SlotInput['types'] => {
  const mapped: SlotInput['types'] = [];
  const byParent: Record<string, SlotInput['types']> = {};
  types
    .filter((it) => it.parentId)
    .forEach((type) => {
      const parentId = type.parentId!;
      if (!byParent[parentId]) {
        byParent[parentId] = [];
      }
      byParent[parentId].push({
        title: type.title,
        value: type.id,
      });
    });
  types
    .filter((it) => !it.parentId)
    .forEach((type) => {
      mapped.push({
        title: type.title,
        value: type.id,
        children: byParent[type.id],
      });
    });
  return mapped;
};
